import React, { useCallback } from 'react';
import { Button } from '@tanium/react-button';
import { FormElementContainer, Modal, ModalProps } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { useTranslation } from './localization';

export interface Props
  extends Pick<ModalProps, 'isOpen' | 'onRequestClose' | 'onAfterClose' | 'size'> {
  /**
   * Label to display on Cancel button, defaults to 'Cancel'.
   */
  cancelLabel?: string;
  /**
   * Content to appear in modal between headers and footer buttons.
   */
  children?: React.ReactNode;
  /**
   * Label to display on Confirm button, defaults to 'Confirm'.
   */
  confirmLabel?: string;
  /**
   * Header content in modal.
   */
  header?: React.ReactNode;
  /**
   * Whether the Confirm button should be disabled or not.
   * Defaults to false.
   */
  isConfirmDisabled?: boolean;
  /**
   * Callback for click of Cancel button.
   */
  onCancel: () => void;
  /**
   * Callback for click of Confirm button.
   */
  onConfirm: () => void;
  /**
   * Subheader content in modal.
   */
  subheader?: React.ReactNode;
}

/**
 * A modal dialog with a confirm and a cancel button.
 */
export const ConfirmModal = ({
  cancelLabel,
  children,
  confirmLabel,
  header,
  isConfirmDisabled = false,
  isOpen,
  onCancel,
  onConfirm,
  onRequestClose,
  onAfterClose,
  subheader,
  size,
}: Props) => {
  const { t } = useTranslation();

  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterClose={onAfterClose}
      size={size}
      container={FormElementContainer}
      header={header}
      subheader={subheader}
      footer={
        <Stack inline itemSpacing={1}>
          <Button type="submit" onClick={handleConfirm} disabled={isConfirmDisabled}>
            {confirmLabel ?? t('Confirm')}
          </Button>
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {cancelLabel ?? t('Cancel')}
          </Button>
        </Stack>
      }
    >
      {children}
    </Modal>
  );
};
